import EmptyState from '@/components/shared/EmptyState';
import PageHeader from '@/components/shared/PageHeader';
import { canPayInvoice, payableLabel, payJobIds } from '@/lib/invoicePayment';
import { formatRegionMoneyMinor, getActiveRegionConfig } from '@/lib/regionConfig';
import supabase from '@/lib/supabaseClient';
import { invoicesApi, paymentsApi } from '@/services/api';
import { Clock, CreditCard, Download, Receipt, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

const API_BASE = (process.env.REACT_APP_API_BASE_URL || '').trim();


const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const isOverdue = (dueDateIso, paidAt) => {
  if (paidAt) return false;
  if (!dueDateIso) return false;
  return new Date(dueDateIso) < new Date();
};

const StatusBadge = ({ paidAt, dueDate }) => {
  if (paidAt) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800">
        Paid
      </span>
    );
  }
  if (isOverdue(dueDate, paidAt)) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700">
        Overdue
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800">
      Unpaid
    </span>
  );
};

const MAX_ROWS_SHOWN = 3;

// The Job ID, company and services of each company an invoice covers (one row, or one per company when consolidated).
const InvoiceJobs = ({ rows, fallbackName, serviceCount }) => {
  const [expanded, setExpanded] = useState(false);
  if (!rows || rows.length === 0) {
    return (
      <div>
        <div className="font-medium">{fallbackName || '—'}</div>
        <div className="text-xs text-muted-foreground">{serviceCount} service{serviceCount !== 1 ? 's' : ''}</div>
      </div>
    );
  }
  const shown = expanded ? rows : rows.slice(0, MAX_ROWS_SHOWN);
  return (
    <div className="space-y-2">
      {shown.map((row) => (
        <div key={row.job_id || row.job_ref}>
          <div className="flex items-baseline gap-2">
            <span className="font-mono text-xs text-muted-foreground">{row.job_ref}</span>
            <span className="font-medium">{row.entity_name || fallbackName || '—'}</span>
          </div>
          <div className="text-xs text-muted-foreground">{(row.services || []).join(' · ')}</div>
        </div>
      ))}
      {rows.length > MAX_ROWS_SHOWN && (
        <button
          type="button"
          onClick={() => setExpanded((open) => !open)}
          className="text-xs underline text-muted-foreground hover:text-foreground"
          data-testid="toggle-invoice-rows"
        >
          {expanded ? 'Show fewer' : `Show all ${rows.length} companies`}
        </button>
      )}
    </div>
  );
};

const Invoices = () => {
  const regionConfig = getActiveRegionConfig();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null); // invoice_id being downloaded
  const [payingId, setPayingId] = useState(null); // invoice_id being sent to the payment page
  const [confirmingPayment, setConfirmingPayment] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await invoicesApi.list();
      setInvoices(data.invoices || []);
    } catch (err) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Back from the payment page: confirm the payment, then reload so the invoice shows as paid.
  const [searchParams, setSearchParams] = useSearchParams();
  const returnedSessionId = searchParams.get('stripe_session_id') || searchParams.get('pay_ref');
  useEffect(() => {
    if (!returnedSessionId) return undefined;
    let cancelled = false;
    setConfirmingPayment(true);
    paymentsApi.verifyReturn(searchParams)
      .then(async (result) => {
        if (cancelled) return;
        if (result.paid) {
          toast.success('Payment received. Thank you - there is no need to pay again.');
        } else if (result.processing) {
          toast.info('Your payment is being processed. Please do not pay again - this page will update shortly.');
        } else {
          toast.error('Payment was not completed. You have not been charged.');
        }
        await load();
      })
      .catch(() => {
        if (!cancelled) toast.error('We could not confirm your payment yet. If you were charged, please do not pay again - contact support.');
      })
      .finally(() => {
        if (cancelled) return;
        setConfirmingPayment(false);
        setSearchParams({}, { replace: true });
      });
    return () => { cancelled = true; };
  }, [returnedSessionId]);

  const handlePay = async (invoice) => {
    if (payingId || confirmingPayment) return;
    setPayingId(invoice.id);
    try {
      // The server prices the checkout from the job, so only the job and where to come back to are sent.
      const { url } = await paymentsApi.startCheckout({
        job_ids: payJobIds(invoice),
        return_url: `${window.location.origin}${window.location.pathname}`,
      });
      window.location.href = url;
    } catch (err) {
      if (err.status === 409) {
        toast.info(err.responseDetail || 'This order has already been paid.');
        await load();
      } else {
        toast.error(err.message || 'Could not start payment. Please try again.');
      }
      setPayingId(null);
    }
  };

  const handleDownload = async (invoice) => {
    setDownloading(invoice.id);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const response = await fetch(`${API_BASE}/api/invoices/${invoice.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error(`Download failed (${response.status})`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoice.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message || 'Download failed');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div data-testid="invoices-page">
      <PageHeader
        title="Invoices"
        subtitle="View and download your invoice history"
        actions={
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-border bg-background hover:bg-muted transition-colors"
            data-testid="refresh-invoices-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        }
      />

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : invoices.length === 0 ? (
        <div className="bg-card border border-border p-6">
          <EmptyState
            icon={Receipt}
            title="No Invoices Yet"
            description="Invoices are generated when a credit request is approved. They will appear here once available."
          />
        </div>
      ) : (
        <div className="border border-border overflow-hidden">
          <table className="w-full text-sm" data-testid="invoices-table">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Invoice</th>
                <th className="text-left px-4 py-3 font-medium">Job ID · Company · Services</th>
                <th className="text-left px-4 py-3 font-medium">Amount</th>
                <th className="text-left px-4 py-3 font-medium">Due Date</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="text-left px-4 py-3 font-medium">Issued</th>
                <th className="text-left px-4 py-3 font-medium">Download</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {invoices.map((inv) => (
                <tr key={inv.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3 font-mono font-medium text-xs">
                    {inv.invoice_number}
                  </td>
                  <td className="px-4 py-3">
                    <InvoiceJobs rows={inv.rows} fallbackName={inv.company_name} serviceCount={(inv.services || []).length} />
                  </td>
                  <td className="px-4 py-3 font-mono font-semibold">
                    {formatRegionMoneyMinor(inv.total_pence, regionConfig)}
                    {inv.kind === 'consolidated' && inv.discount_pence > 0 && (
                      <div className="text-xs font-normal text-muted-foreground">
                        after {formatRegionMoneyMinor(inv.discount_pence, regionConfig)} discount
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className={isOverdue(inv.due_date, inv.paid_at) ? 'text-red-600 font-semibold' : ''}>
                      {formatDate(inv.due_date)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {canPayInvoice(inv) ? (
                      <button
                        onClick={() => handlePay(inv)}
                        disabled={Boolean(payingId) || confirmingPayment}
                        title="Pay this invoice now"
                        className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium border transition-colors disabled:opacity-60 ${
                          payableLabel(inv) === 'Overdue'
                            ? 'bg-red-100 text-red-700 border-red-200 hover:bg-red-200'
                            : 'bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-200'
                        }`}
                        data-testid={`pay-invoice-${inv.id}`}
                      >
                        <CreditCard className="w-3 h-3" />
                        {payingId === inv.id ? 'Redirecting…' : `${payableLabel(inv)} · Pay now`}
                      </button>
                    ) : (
                      <StatusBadge paidAt={inv.paid_at} dueDate={inv.due_date} />
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {formatDate(inv.generated_at)}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleDownload(inv)}
                      disabled={downloading === inv.id}
                      className="flex items-center gap-1 px-2 py-1 text-xs border border-border hover:bg-muted transition-colors disabled:opacity-50"
                      data-testid={`download-invoice-${inv.id}`}
                    >
                      {downloading === inv.id ? (
                        <Clock className="w-3.5 h-3.5 animate-pulse" />
                      ) : (
                        <Download className="w-3.5 h-3.5" />
                      )}
                      PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Invoices;
